import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  Course,
  CourseContent,
  CourseContentType,
  CourseStatusAttemptEnum,
  ResourceType,
} from '@/client/courses';
import { LearnerCourseDispatchResponse } from '@/client/learner-course';
import { QuizType } from '@/client/quizzes';
import { ContinueOrFinishButton } from '@/components/courses/ContinueOrFinishButton';
import { CourseLanguageSelectScreen } from '@/components/courses/CourseLanguageSelectScreen';
import { Quiz } from '@/components/quiz/Quiz';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { ScormProvider } from '@/scorm';
import { ScormVersion } from '@/scorm/types';
import { determineSelectedLanguage } from '@/scorm/utils/content';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { ContentPDFViewer } from '@/ui/pdf-viewer/ContentPDFViewer';
import { FlexContainer } from '@/ui/styled-ui';
import { isMobile } from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';

import { getCurrentVideoFile, isRtlLanguage } from '@/utils/helpers';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { CoursesActionHeader } from '../../pages/courses/CoursesActionHeader';
import { CoursesTabs } from '../../pages/courses/CoursesTabs';
import { QuizFeedback } from '../quiz/QuizFeedback';
import { SlideTextImageTemplateViewer } from '../slides/learner/slide-templates/SlideTextImageTemplateViewer';
import { VideoFallbackMessage } from './VideoFallbackMessage';

const StyledWrapper = styled(FlexContainer).attrs({
  translate: 'no',
})<{ isMobile: boolean }>`
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--beige-main);
  border-radius: var(--default-border-radius);
  border: 1px solid var(--gray-dark);
  position: relative;
  flex: 1;

  ${({ isMobile }) =>
    isMobile &&
    css`
      height: auto;
      margin-top: var(--small-padding);
      margin-bottom: var(--small-padding);
    `}
`;

const StyledGrayFlexContainer = styled(FlexContainer)`
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  background-color: var(--beige-main);
  padding: var(--default-padding);
`;

const StyledContentContainer = styled.div`
  position: relative;
  display: flex;
  margin: auto;
  width: 70%;

  &::before {
    content: '';
    width: 0;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    display: block;
  }
`;

const StyledIframe = styled.iframe`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border: none;
`;

const StyledVideo = styled.video`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border: none;
`;

const StyledNextButton = styled(AppButton)`
  position: absolute;
  right: -48px;
  top: 50%;
`;

const StyledSpinnerDiv = styled.div`
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: var(--white-main);
  padding: var(--default-padding);
  border-radius: var(--default-border-radius);
  opacity: 0.8;
  z-index: 9;
`;

const hasScormContent = (modules: CourseContent[]): boolean => {
  return modules.some(
    (module) =>
      module.type === CourseContentType.RESOURCE &&
      module.resource?.type === ResourceType.SCORM,
  );
};

export const CourseContentPreview: React.FC<{
  course: Course;
  isEmbedded?: boolean;
}> = ({ course, isEmbedded = false }) => {
  const [isContentStarted, setIsContentStarted] = useState<boolean>(false);
  const currentUser = useAppSelector(selectCurrentUser);
  const { t } = useTranslation();
  const toast = useToast();
  const location = useLocation();

  const returnUrl = (location.state as { from?: string })?.from;

  const [status, setStatus] = useState<CourseStatusAttemptEnum>();
  const [modules, setModules] = useState<CourseContent[]>([]);
  const [currentModule, setCurrentModule] = useState<number>(0);
  const [currentModuleContent, setCurrentModuleContent] =
    useState<CourseContent | null>(null);
  const [scormDetails, setScormDetails] =
    useState<LearnerCourseDispatchResponse | null>(null);
  const [hasConfirmedRead, setHasConfirmedRead] = useState<boolean>(false);

  const [selectedLanguage, setSelectedLanguage] = useState<LanguagesEnum>(
    // Set initial preselected language if not available set to English
    determineSelectedLanguage(currentUser, course),
  );
  const isRtl = isRtlLanguage(selectedLanguage);

  const [isSubmittingStatus] = useState<boolean>(false);
  const [isCurrentModuleCompleted, setIsCurrentModuleCompleted] =
    useState<boolean>(false);

  const getLanguage = () => {
    if (course?.selfHosted && course?.courseLanguages?.length === 1) {
      return course?.courseLanguages[0];
    }

    if (course && !course?.selfHosted) {
      return LanguagesEnum.EN;
    }

    return selectedLanguage;
  };

  const [isUrlLoading, setIsUrlLoading] = useState<boolean>(false);

  const fetchData = async () => {
    const courseLanguage = getLanguage();

    if (course && courseLanguage) {
      const modules = course.courseContents?.sort(
        (a, b) => a.position - b.position,
      );

      setModules(modules || []);
    }
  };

  useEffect(() => {
    const fetchCurrentModule = async () => {
      if (!course?.id || !modules?.[currentModule]?.id) return;

      try {
        const module = modules[currentModule];

        if (module.type === CourseContentType.RESOURCE) {
          const resourceData = await client.courses.getCourseResource(
            course.id,
            module.resource?.id || '',
          );

          if (resourceData) {
            setCurrentModuleContent({
              ...module,
              resource: resourceData,
            });
          }
        } else if (module.type === CourseContentType.QUIZ) {
          const quizData = await client.courses.getCourseQuiz(
            course.id,
            module.quiz?.id || '',
          );

          if (quizData) {
            setCurrentModuleContent({
              ...module,
              quiz: quizData,
            });
          }
        }
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    };

    fetchCurrentModule();
  }, [currentModule, course?.id, modules]);

  useEffect(() => {
    // Reset completion state when module changes
    setIsCurrentModuleCompleted(false);
    setHasConfirmedRead(false);
  }, [currentModule]);

  useEffect(() => {
    const fetchScormDetails = async () => {
      if (!course?.id || !currentModuleContent?.resource?.id) return;

      try {
        setIsUrlLoading(true);
        const scormDetails = await client.courses.getResourceDispatchUrl(
          course?.id,
          currentModuleContent.resource.id,
          selectedLanguage,
        );

        setScormDetails(scormDetails);
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      } finally {
        setIsUrlLoading(false);
      }
    };

    if (currentModuleContent?.resource?.type === ResourceType.SCORM) {
      fetchScormDetails();
    }
  }, [currentModuleContent, modules, selectedLanguage]);

  const handleModuleCompletion = async () => {
    if (!course?.id || !selectedLanguage) return;

    if (
      currentModuleContent?.quiz?.type === QuizType.TRADITIONAL ||
      currentModule === modules.length - 1 ||
      (!modules.some((module) => module.quiz?.type === QuizType.TRADITIONAL) &&
        !hasScormContent(modules))
    ) {
      toast?.success(t('toast.success'), t('content.completed.success'));
    }

    if (currentModule !== modules.length - 1) {
      setIsCurrentModuleCompleted(false);
      setCurrentModule(currentModule + 1);
    }
  };

  const handleBackToCourse = async () => {
    setCurrentModuleContent(null);
    setCurrentModule(0);
  };

  useEffect(() => {
    if (!course || isUrlLoading) return;

    if (course?.selfHosted && course?.courseLanguages?.length === 1) {
      setIsContentStarted(true);
    }
  }, [course]);

  useEffect(() => {
    if (!course || isUrlLoading) return;

    if (course?.courseContents?.length) {
      fetchData();
      setCurrentModule(0);
    }
  }, [course?.id, selectedLanguage]);

  const isPdfReadConfirmationRequired =
    modules?.[currentModule]?.resource?.type === ResourceType.PDF &&
    !!modules?.[currentModule]?.resource?.requiresReadConfirmation;

  const continueOrFinishButton = (
    <ContinueOrFinishButton
      currentModule={currentModule}
      modules={modules}
      isCurrentModuleCompleted={isCurrentModuleCompleted}
      isContentStarted={isContentStarted}
      attemptLanguage={selectedLanguage}
      shouldWrapInStickyContainer={
        modules?.[currentModule]?.type !== CourseContentType.PRESENTATION &&
        modules?.[currentModule]?.resource?.type !== ResourceType.PDF
      }
      isDisabled={isPdfReadConfirmationRequired && !hasConfirmedRead}
      onContinue={() => {
        setCurrentModule(currentModule + 1);
        setIsCurrentModuleCompleted(false);
      }}
      onFinish={handleModuleCompletion}
      rtl={isRtl}
    />
  );

  useEffect(() => {
    const contentElement = document.querySelector('.content');

    if (contentElement) {
      contentElement.classList.add('content-viewer-active');

      return () => {
        contentElement.classList.remove('content-viewer-active');
      };
    }
  }, []);

  return (
    <>
      {!course && (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
          <h3>{t('content.loading')}</h3>
        </FlexContainer>
      )}
      {course && (
        <FlexContainer
          direction="column"
          align="flex-start"
          justify="flex-start"
          height="100%"
        >
          {!isEmbedded && (
            <>
              <CoursesActionHeader course={course} returnUrl={returnUrl} />
              {course && <CoursesTabs course={course} />}
            </>
          )}

          {/* QUIZ */}
          {isContentStarted &&
            modules[currentModule]?.quiz?.type === QuizType.TRADITIONAL && (
              <StyledWrapper isMobile={isMobile()}>
                <Quiz
                  course={course}
                  quizId={modules?.[currentModule]?.quiz?.id}
                  attemptLanguage={selectedLanguage || LanguagesEnum.EN}
                  hasPreviousModules={currentModule > 0}
                  isPreview
                  onFinish={handleModuleCompletion}
                  handleBackToCourse={handleBackToCourse}
                  rtl={isRtl}
                />
              </StyledWrapper>
            )}

          {/* VIDEO */}
          {isContentStarted &&
            modules?.[currentModule]?.type === CourseContentType.RESOURCE &&
            currentModuleContent?.resource?.type === ResourceType.VIDEO &&
            (() => {
              const videoResult = getCurrentVideoFile(
                currentModuleContent,
                selectedLanguage,
              );
              if (!videoResult) return null;

              return (
                <StyledWrapper isMobile={isMobile()}>
                  <FlexContainer
                    direction="column"
                    style={{
                      width: '70%',
                      margin: 'auto',
                    }}
                  >
                    {videoResult.isFallback && (
                      <VideoFallbackMessage
                        severity="info"
                        text={t('course.video.fallback.playback.v2', {
                          fallbackLanguage:
                            languageDisplayLabel[
                              videoResult.fallbackLanguage ?? LanguagesEnum.EN
                            ],
                        })}
                        className="w-full mb-2"
                      />
                    )}
                    <StyledContentContainer
                      style={{ width: '100%', margin: 0 }}
                    >
                      <StyledVideo
                        playsInline
                        webkit-playsinline
                        src={videoResult.file.signedUrl}
                        controls
                        controlsList="nodownload"
                        onContextMenu={(e) => e.preventDefault()}
                        autoPlay
                        onEnded={() => {
                          setIsCurrentModuleCompleted(true);
                          if (document.fullscreenElement) {
                            document.exitFullscreen();
                          }
                        }}
                      />
                    </StyledContentContainer>
                  </FlexContainer>

                  {continueOrFinishButton}
                </StyledWrapper>
              );
            })()}

          {/* SLIDE DECK VIEWER */}
          {/* 
            TODO: After templates are introduced we will
            add handler to dynamically switch between templates.
            Currently we are hardcoding the SlideTextImageTemplateViewer for
            all slide types.
            PresentationId will be passed for this new template switcher
            which will fetch the presentation data from the API instead of the template viewer itself.
          */}
          {isContentStarted &&
            modules?.[currentModule]?.type ===
              CourseContentType.PRESENTATION && (
              <StyledWrapper isMobile={isMobile()} className="p-0">
                <SlideTextImageTemplateViewer
                  presentationId={modules?.[currentModule]?.presentation?.id}
                  courseId={course?.id}
                  attemptLanguage={selectedLanguage}
                  hasNextModule={currentModule !== modules.length - 1}
                  hasPreviousModule={currentModule > 0}
                  onPreviousModule={() => {
                    if (currentModule > 0) {
                      setCurrentModule(currentModule - 1);
                    }
                  }}
                  onComplete={handleModuleCompletion}
                  continueOrFinishButton={continueOrFinishButton}
                  onLastPageReached={() => setIsCurrentModuleCompleted(true)}
                  rtl={isRtl}
                />
              </StyledWrapper>
            )}

          {/* PDF */}
          {isContentStarted &&
            modules?.[currentModule]?.type === CourseContentType.RESOURCE &&
            currentModuleContent?.resource?.type === ResourceType.PDF && (
              <StyledWrapper isMobile={isMobile()}>
                <ContentPDFViewer
                  file={
                    currentModuleContent.resource?.translations?.find(
                      (t) => t.language === selectedLanguage,
                    )?.file ?? null
                  }
                  hasPreviousModules={currentModule > 0}
                  hideNextButton={currentModule === modules.length - 1}
                  continueOrFinishButton={continueOrFinishButton}
                  requiresReadConfirmation={isPdfReadConfirmationRequired}
                  hasConfirmedRead={hasConfirmedRead}
                  attemptLanguage={selectedLanguage}
                  onReadConfirmationChange={setHasConfirmedRead}
                  onPreviousModule={() => {
                    if (currentModule > 0) {
                      setCurrentModule(currentModule - 1);
                    }
                  }}
                  onLastPageReached={() => setIsCurrentModuleCompleted(true)}
                />
              </StyledWrapper>
            )}

          {/* FEEDBACK */}
          {isContentStarted &&
            currentModuleContent?.quiz?.type === QuizType.FEEDBACK && (
              <StyledWrapper isMobile={isMobile()}>
                <QuizFeedback
                  course={course}
                  quizFeedbackId={currentModuleContent?.quiz?.id}
                  isPreview
                  attemptLanguage={selectedLanguage}
                  onComplete={handleModuleCompletion}
                  rtl={isRtl}
                />
              </StyledWrapper>
            )}

          {(isContentStarted || !course.selfHosted) &&
            !isUrlLoading &&
            !!course?.courseContents?.length &&
            !!modules?.length &&
            scormDetails &&
            currentModuleContent?.resource?.type === ResourceType.SCORM &&
            currentUser && (
              <StyledWrapper direction="column" isMobile={isMobile()}>
                <StyledContentContainer>
                  <ScormProvider
                    course={course}
                    version={scormDetails?.version as ScormVersion}
                    user={currentUser}
                    isPreview={true}
                    setStatus={setStatus}
                    isLastModule={currentModule === modules.length - 1}
                    setNextModule={() => setCurrentModule(currentModule + 1)}
                  >
                    {isSubmittingStatus && (
                      <StyledSpinnerDiv>
                        <ProgressSpinner />
                        <span>{t('content.processing.results')}</span>
                      </StyledSpinnerDiv>
                    )}

                    <StyledIframe
                      src={scormDetails?.url}
                      style={{
                        visibility: isSubmittingStatus ? 'hidden' : 'visible',
                      }}
                    />
                  </ScormProvider>
                  {status === CourseStatusAttemptEnum.COMPLETED &&
                    currentModule !== modules.length - 1 && (
                      <StyledNextButton
                        size="xs"
                        icon="pi pi-chevron-right"
                        onClick={() => setCurrentModule(currentModule + 1)}
                      />
                    )}
                </StyledContentContainer>
              </StyledWrapper>
            )}
          {!isContentStarted &&
            course.selfHosted &&
            !!course?.courseContents?.length && (
              <CourseLanguageSelectScreen
                course={course}
                initialLanguage={selectedLanguage}
                isLoading={isUrlLoading}
                isDisabled={!modules?.length}
                setIsCourseStarted={setIsContentStarted}
                setSelectedLanguage={setSelectedLanguage}
              />
            )}
          {course && !course?.courseContents?.length && (
            <StyledGrayFlexContainer direction="column" flex="1" height="100%">
              <h3>{t('course.scorm.preview.noFiles')}</h3>
            </StyledGrayFlexContainer>
          )}
        </FlexContainer>
      )}
    </>
  );
};
